import { useState } from "react";
import { Link } from "react-router-dom";
import { useMedia } from "@/context/MediaContext";
import { useCart, VARIANTS } from "@/context/CartContext";

export const familyTags = (p) => (p.families?.length ? p.families : (p.accord || "").split("·").map((s) => s.trim()).filter(Boolean));

export const productImage = (p, img) => (p.live ? p.image : img(`product_${p.slug.replace(/-/g, "_")}`, p.image));

const SIZE_KEYS = ["50ml", "10ml"];
const BADGE = "absolute top-4 left-4 px-3 py-1.5 text-[0.58rem] uppercase tracking-[0.24em]";

const CardBadge = ({ p, soldOut }) => {
  if (soldOut) return <span data-testid={`sold-out-badge-${p.slug}`} className={`${BADGE} bg-[#111]/85 text-[#F7F3EE]`}>Sold Out</span>;
  if (p.bestseller) return <span data-testid={`bestseller-badge-${p.slug}`} className={`${BADGE} bg-[#BA8D53] text-white`}>Best Seller</span>;
  if (p.isNew) return <span data-testid={`new-badge-${p.slug}`} className={`${BADGE} bg-[#F7F3EE] text-[#111]`}>New</span>;
  return null;
};

const CardImage = ({ p, soldOut }) => {
  const img = useMedia();
  const primary = productImage(p, img);
  const secondary = p.images?.[1] || p.aestheticImage;
  return (
    <Link to={`/products/${p.slug}`} data-testid={`product-image-link-${p.slug}`} className="relative block overflow-hidden bg-[#EFE9E1]">
      <div className="aspect-[4/5]">
        <img src={primary} alt={p.name} loading="lazy" className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${secondary ? "group-hover:opacity-0" : ""} ${soldOut ? "opacity-60 grayscale" : ""}`} />
        {secondary && <img src={secondary} alt={`${p.name} editorial`} loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-0 scale-[1.04] transition-all duration-700 group-hover:opacity-100 group-hover:scale-100" />}
      </div>
      <CardBadge p={p} soldOut={soldOut} />
    </Link>
  );
};

const SizeToggle = ({ p, sizes, variant, onChange }) => (
  <div className="flex items-center gap-1.5" data-testid={`size-toggle-${p.slug}`}>
    {sizes.map((k) => (
      <button
        key={k}
        type="button"
        onClick={() => onChange(k)}
        data-testid={`size-option-${p.slug}-${k}`}
        className={`whitespace-nowrap text-[0.6rem] uppercase tracking-[0.16em] px-2.5 py-2 md:py-1 border transition-colors duration-300 ${variant === k ? "border-[#111] bg-[#111] text-[#F7F3EE]" : "border-black/15 text-black/60 hover:border-black/50"}`}
      >
        {k === "combo" ? "50 + 10 ml" : k.replace("ml", " ml")}
      </button>
    ))}
  </div>
);

const FamilyTags = ({ p }) => (
  <p className="mt-3 text-[0.62rem] uppercase tracking-[0.2em] text-[#BA8D53] flex flex-wrap gap-x-2" data-testid={`product-families-${p.slug}`}>
    {familyTags(p).map((t, i) => (
      <span key={t}>{i > 0 && <span className="text-black/25 mr-2">·</span>}{t}</span>
    ))}
  </p>
);

const AddButton = ({ p, variant, soldOut }) => {
  const { addItem } = useCart();
  if (soldOut) return <span data-testid={`sold-out-button-${p.slug}`} className="block w-full text-center border border-black/10 text-black/35 py-3 text-[0.65rem] uppercase tracking-[0.24em]">Sold Out</span>;
  return (
    <button type="button" onClick={() => addItem(p.slug, variant)} data-testid={`add-to-cart-${p.slug}-${variant}`} className="w-full border border-[#111] py-3 text-[0.65rem] uppercase tracking-[0.24em] font-medium hover:bg-[#111] hover:text-[#F7F3EE] transition-colors duration-300">
      Add to Cart
    </button>
  );
};

export default function ProductCard({ p, defaultVariant = "50ml", className = "" }) {
  const [variant, setVariant] = useState(defaultVariant);
  const soldOut = p.available === false;
  const price = p[VARIANTS[variant]?.priceKey];
  const sizes = variant === "combo" ? ["combo"] : SIZE_KEYS.filter((k) => p[VARIANTS[k].priceKey] != null);

  return (
    <article data-testid={`product-card-${p.slug}`} className={`group flex flex-col ${className}`}>
      <CardImage p={p} soldOut={soldOut} />
      <div className="pt-5 flex flex-col flex-1">
        <Link to={`/products/${p.slug}`} className="block">
          <h3 className="font-serif-brand text-2xl md:text-[1.65rem] font-light leading-tight group-hover:text-[#BA8D53] transition-colors duration-300">{p.name}</h3>
        </Link>
        <p className="mt-1 text-xs text-black/50">{variant === "combo" ? "Parfum + Roll-On" : "Parfum"}</p>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <SizeToggle p={p} sizes={sizes} variant={variant} onChange={setVariant} />
          {price != null && <p data-testid={`product-price-${variant}-${p.slug}`} className="text-sm font-medium tabular-nums">₹{Math.round(price).toLocaleString("en-IN")}</p>}
        </div>
        <FamilyTags p={p} />
        <div className="mt-auto pt-5">
          <AddButton p={p} variant={variant} soldOut={soldOut} />
        </div>
      </div>
    </article>
  );
}
